import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { ErrorOutline, InfoOutlined, CheckCircleOutline, WarningAmber } from '@mui/icons-material';
import { motion } from 'framer-motion';
import CustomAlert from '../components/common/CustomAlert';
import { useTranslation } from 'react-i18next';
import { API_ERROR_CODES } from '../config/appConstants';

const initialState = {
    error: null,
    showError: false,
    errorTitle: '',
    errorMessage: '',
    severity: 'error',
    onClose: null,
    autoHideTimeout: null,
    additionalAction: null
};

const actionTypes = {
    SHOW_ERROR: 'SHOW_ERROR',
    HIDE_ERROR: 'HIDE_ERROR',
    SET_ERROR: 'SET_ERROR',
    SET_SUCCESS: 'SET_SUCCESS',
    SET_WARNING: 'SET_WARNING',
    SET_INFO: 'SET_INFO'
};

const errorReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SHOW_ERROR:
            return {
                ...state,
                showError: true,
                errorTitle: action.payload.title || state.errorTitle,
                errorMessage: action.payload.message || state.errorMessage,
                severity: action.payload.severity || state.severity,
                onClose: action.payload.onClose || state.onClose,
                autoHideTimeout: action.payload.autoHideTimeout || state.autoHideTimeout,
                additionalAction: action.payload.additionalAction || state.additionalAction
            };
        case actionTypes.HIDE_ERROR:
            return {
                ...state,
                showError: false,
                additionalAction: null
            };
        case actionTypes.SET_ERROR:
            return {
                ...state,
                error: action.payload.error,
                errorTitle: action.payload.title || 'Error',
                errorMessage: action.payload.message || 'An unexpected error occurred',
                severity: 'error',
                showError: true,
                onClose: action.payload.onClose || null,
                autoHideTimeout: action.payload.autoHideTimeout || null,
                additionalAction: action.payload.additionalAction || null
            };
        case actionTypes.SET_SUCCESS:
            return {
                ...state,
                errorTitle: action.payload.title || 'Success',
                errorMessage: action.payload.message || 'Operation completed successfully',
                severity: 'success',
                showError: true,
                onClose: action.payload.onClose || null,
                autoHideTimeout: action.payload.autoHideTimeout || null,
                additionalAction: action.payload.additionalAction || null
            };
        case actionTypes.SET_WARNING:
            return {
                ...state,
                errorTitle: action.payload.title || 'Warning',
                errorMessage: action.payload.message || 'Please be cautious',
                severity: 'warning',
                showError: true,
                onClose: action.payload.onClose || null,
                autoHideTimeout: action.payload.autoHideTimeout || null,
                additionalAction: action.payload.additionalAction || null
            };
        case actionTypes.SET_INFO:
            return {
                ...state,
                errorTitle: action.payload.title || 'Information',
                errorMessage: action.payload.message || '',
                severity: 'info',
                showError: true,
                onClose: action.payload.onClose || null,
                autoHideTimeout: action.payload.autoHideTimeout || null,
                additionalAction: action.payload.additionalAction || null
            };
        default:
            return state;
    }
};

const ErrorContext = createContext(initialState);

export const ErrorProvider = ({ children }) => {
    const { t } = useTranslation();
    const [state, dispatch] = useReducer(errorReducer, initialState);

    const showError = useCallback((options = {}) => {
        const {
            title = t('error.title'),
            message = t('error.unknown'),
            severity = 'error',
            onClose = null,
            autoHideTimeout = null,
            additionalAction = null
        } = options;

        dispatch({
            type: actionTypes.SHOW_ERROR,
            payload: { title, message, severity, onClose, autoHideTimeout, additionalAction }
        });
    }, [t]);

    const hideError = useCallback(() => {
        dispatch({ type: actionTypes.HIDE_ERROR });

        if (state.onClose && typeof state.onClose === 'function') {
            state.onClose();
        }
    }, [state.onClose]);

    const showSuccess = useCallback((message, options = {}) => {
        dispatch({
            type: actionTypes.SET_SUCCESS,
            payload: {
                title: options.title || t('common.success'),
                message,
                onClose: options.onClose,
                autoHideTimeout: options.autoHideTimeout || 3000,
                additionalAction: options.additionalAction
            }
        });
    }, [t]);

    const showWarning = useCallback((message, options = {}) => {
        dispatch({
            type: actionTypes.SET_WARNING,
            payload: {
                title: options.title || t('common.warning'),
                message,
                onClose: options.onClose,
                autoHideTimeout: options.autoHideTimeout,
                additionalAction: options.additionalAction
            }
        });
    }, [t]);

    const showInfo = useCallback((message, options = {}) => {
        dispatch({
            type: actionTypes.SET_INFO,
            payload: {
                title: options.title || t('common.information'),
                message,
                onClose: options.onClose,
                autoHideTimeout: options.autoHideTimeout,
                additionalAction: options.additionalAction
            }
        });
    }, [t]);

    const handleApiError = useCallback((error, customOptions = {}) => {
        const defaultOptions = {
            title: t('error.title')
        };

        let errorMessage = t('error.unknown');

        if (error.response) {
            const status = error.response.status;
            const responseData = error.response.data;

            if (status === API_ERROR_CODES.UNAUTHORIZED) {
                errorMessage = t('error.unauthorized');
                if (customOptions.handleUnauthorized) {
                    customOptions.handleUnauthorized();
                }
            } else if (status === API_ERROR_CODES.FORBIDDEN) {
                errorMessage = t('error.forbidden');
            } else if (status === API_ERROR_CODES.NOT_FOUND) {
                errorMessage = t('error.notFound');
            } else if (status === API_ERROR_CODES.BAD_REQUEST) {
                if (responseData?.detail?.includes('already exists')) {
                    errorMessage = t('error.duplicate');
                } else if (responseData?.message) {
                    errorMessage = responseData.message;
                } else if (responseData?.detail) {
                    errorMessage = responseData.detail;
                } else {
                    errorMessage = t('error.badRequest');
                }
            } else if (responseData?.message) {
                errorMessage = responseData.message;
            } else if (responseData?.detail) {
                errorMessage = responseData.detail;
            }
        } else if (error.message === 'Network Error') {
            errorMessage = t('error.network');
        }

        const options = {
            ...defaultOptions,
            message: errorMessage,
            ...customOptions,
            error: error
        };

        dispatch({
            type: actionTypes.SET_ERROR,
            payload: options
        });

        if (options.autoHideTimeout) {
            setTimeout(() => {
                hideError();
            }, options.autoHideTimeout);
        }

        return error;
    }, [t, hideError]);

    const renderErrorIcon = useCallback(() => (
        <motion.div
            animate={{ rotate: [0, -15, 15, -15, 0], scale: [1, 1.1, 1.1, 1.1, 1] }}
            transition={{ duration: 0.5 }}
        >
            <ErrorOutline color="error" fontSize="large" />
        </motion.div>
    ), []);

    const renderSuccessIcon = useCallback(() => (
        <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
        >
            <CheckCircleOutline color="primary" fontSize="large" />
        </motion.div>
    ), []);

    const renderInfoIcon = useCallback(() => (
        <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring" }}
        >
            <InfoOutlined color="info" fontSize="large" />
        </motion.div>
    ), []);

    const renderWarningIcon = useCallback(() => (
        <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: 3, duration: 0.5 }}
        >
            <WarningAmber color="warning" fontSize="large" />
        </motion.div>
    ), []);

    const getIcon = useCallback(() => {
        switch (state.severity) {
            case 'error':
                return renderErrorIcon();
            case 'success':
                return renderSuccessIcon();
            case 'info':
                return renderInfoIcon();
            case 'warning':
                return renderWarningIcon();
            default:
                return null;
        }
    }, [state.severity, renderErrorIcon, renderSuccessIcon, renderInfoIcon, renderWarningIcon]);

    useEffect(() => {
        if (state.autoHideTimeout && state.showError) {
            const timer = setTimeout(() => {
                hideError();
            }, state.autoHideTimeout);

            return () => clearTimeout(timer);
        }
    }, [state.autoHideTimeout, state.showError, hideError]);

    return (
        <ErrorContext.Provider
            value={{
                ...state,
                showError,
                hideError,
                showSuccess,
                showWarning,
                showInfo,
                handleApiError
            }}
        >
            {children}

            <CustomAlert
                open={state.showError}
                title={state.errorTitle}
                message={state.errorMessage}
                onConfirm={hideError}
                onClose={hideError}
                severity={state.severity}
                icon={getIcon()}
                animate={true}
                isConfirm={!state.additionalAction}
                additionalAction={state.additionalAction}
                confirmText={t('common.confirm')}
            />
        </ErrorContext.Provider>
    );
};

export const useError = () => {
    const context = useContext(ErrorContext);
    if (context === undefined) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};

export default ErrorContext;